import { Text, View, TouchableOpacity } from 'react-native';
import { useUnistyles } from 'react-native-unistyles';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Toast from 'react-native-toast-message'
import { Keyboard } from 'react-native';
import { PatternBackground } from '@/components/General/Backgrounds';
import { LegendList } from "@legendapp/list";
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { supabase } from '@/data/client';
import { clearOrgIDCache, getCachedOrgID, getOrganization, getOrganizationEntitlements, isBillingAllowed, logOutOfOrganization, setOrganizationSelectionActive } from '@/data/organization';
import { isBillingRestricted, isSetupPending } from '@/utils/organizationEntitlements';
import { getLoggedInUserInfo } from '@/data/people';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import { ActivityIndicator } from 'react-native-paper';
import useLoader from '@/utils/hooks/useLoader';
import CutCard from '@/components/General/CutCard';
import { AuthHeader, AuthInput as Input, AuthButton as SolidButton } from '@/components/General/AuthPage';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { saveRegistration, acceptInvite } from '@/data/registrations';
import { Image } from 'expo-image';
import { signOut } from '@/utils/signOut';
import { ensureFreshSession } from '@/utils/authRefresh';
import ErrorHandler from '@/utils/ErrorHandler';
import * as Sentry from '@sentry/react-native';
import useAuthStore from '@/data/store/useAuthStore';
import usePlatform from '@/utils/hooks/usePlatform';
import { openOrgSignup } from '@/utils/orgSignup';
import SheetModal from '@/components/General/SheetModal';
import { Redirect, useRouter } from 'expo-router';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import Chip from '@/components/General/Chip';
import { restoreSessionWithRetry } from '@/utils/authRefresh';

const OrgSelectionScreen = () => {
    const insets = useSafeAreaInsets();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [hasOrganizationSelected, setHasOrganizationSelected] = useState(false);
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);
    const [currentAuthUser, setCurrentAuthUser] = useState({});
    const [organizationHandle, setOrganizationHandle] = useState("");
    const [joinModalOpen, setJoinModalOpen] = useState(false);

    const [signingIn, setSigningIn] = useState(false);

    const auth = useAuthStore();
    const router = useRouter();
    const queryClient = useQueryClient()

    const {theme: {colors, fonts, scheme}} = useUnistyles();
    const { isWeb } = usePlatform();
    const loader = useLoader();

    const { data: organizations, isPending } = Query.organization.byUserID({userID: currentAuthUser?.id, options: {enabled: !!currentAuthUser?.id}})
    const { data: pendingOrganizations, isPending: fetchingPendingRequests } = Query.registrations.byRequestingUser({requestingUserID: currentAuthUser?.id, options: {enabled: !!currentAuthUser?.id}})
    const { data: pendingInvites, isPending: fetchingInvites } = Query.invites.forUser({email: currentAuthUser?.email, options: {enabled: !!currentAuthUser?.email}})

    const [acceptingInviteID, setAcceptingInviteID] = useState(null);

    const saveRegistrationMutation = useMutation({
        mutationFn: saveRegistration,
        onSuccess: (data) => {
            if(data?.duplicate){
                setSigningIn(false);
                return;
            }

            setSigningIn(false);
            setOrganizationHandle("");

            setJoinModalOpen(false);
            Toast.delayShow({type: 'success', text2: "Your request has been sent to the organization admins! You will be notified if they approve your request to join."});

            queryClient.invalidateQueries({queryKey: QueryKeys.organization.all()})
            queryClient.invalidateQueries({queryKey: QueryKeys.registrations.all()})
        }
    })

    const handleAcceptInvite = async (invite) => {
        setAcceptingInviteID(invite.id);
        try {
            await acceptInvite({
                inviteID: invite.id,
                organizationID: invite.organizationID,
                personID: invite.personID,
                userID: currentAuthUser?.id
            });

            queryClient.invalidateQueries({queryKey: QueryKeys.organization.all()})
            queryClient.invalidateQueries({queryKey: QueryKeys.invites.forUser(currentAuthUser?.email)})

            await signIntoOrganization({ id: invite.organizationID });
        } catch {
            //nothing
        } finally {
            setAcceptingInviteID(null);
        }
    }

    const checkAuth = async () => {
        // Resilient restore — a transient network failure resolves getSession()
        // as { session: null, error } WITHOUT removing the persisted session.
        // Treating that as logged out kicked valid users to /auth/login on a
        // slow network. Retry transient errors; only a genuinely absent/invalid
        // session returns null. Bounded: if it never recovers, the user still
        // gets a usable screen rather than a spinner forever.
        const session = await restoreSessionWithRetry({ maxAttempts: 5 });
        const authOrganizationID = session?.user?.app_metadata?.organizationID;

        if(session?.user?.id){
            setCurrentAuthUser(session.user);
            setIsAuthenticated(true);

            if(authOrganizationID){
                setHasOrganizationSelected(true);
            }
        }
        else{
            setCurrentAuthUser({});
            setIsAuthenticated(false);
        }

        setIsCheckingAuth(false);
    }

    useEffect(() => {
        setOrganizationSelectionActive(true);
        Toast.hide();
        checkAuth();

        return () => setOrganizationSelectionActive(false);
    }, [])

    // useEffect(() => {
    //     navigation.setOptions({
    //         headerRight: () => (
    //             <View style={{flexDirection:"row"}}>
    //                 <TouchableOpacity onPress={() => router.push("/settings")}>
    //                     <Unicon name="settings-outline" size={25} colorVariant={"text"} style={{marginRight:20}}/>
    //                 </TouchableOpacity>
    //             </View>
    //         ),
    //     });
    // }, [navigation])

    const signIntoOrganization = async (organization) => {
        loader.enable(false, true);
        clearOrgIDCache();
        queryClient.invalidateQueries();

        // Plan gate — softwarePlan is authoritative for entry. Free and Paid
        // orgs both pass this pre-check; accessState (setup_pending /
        // billing_restricted) is evaluated post-login through the entitlements
        // RPC, which needs the org claim minted into the JWT first.
        // billingStatus remains the fallback when the org row predates plan
        // columns. Org details may be missing (invite-accept path passes only
        // {id}); fetch if so. Fail closed.
        let orgDetails = organization;
        if(orgDetails?.softwarePlan === undefined && orgDetails?.id){
            orgDetails = (await getOrganization(orgDetails.id)) || orgDetails;
        }

        const entryAllowed = orgDetails?.softwarePlan === 'free' || orgDetails?.softwarePlan === 'paid'
            || (orgDetails?.softwarePlan === undefined && isBillingAllowed(orgDetails?.billingStatus));

        if(!entryAllowed){
            Toast.show({
                type: 'error',
                text1: "Subscription Required",
                text2: "This organization doesn't have an active subscription. Complete checkout at reachchms.com to continue.",
                visibilityTime: 6000
            });
            loader.disable();
            return;
        }

        const {data, error} = await supabase.rpc('log_into_org', {organization_id: organization.id});

        if(!error && data == 'OK'){
            setHasOrganizationSelected(true);
            // E2E contexts stub ensureFreshSession to a getSession() no-op
            // (protects the SHARED storage-state refresh token from rotation).
            // This session just came from a fresh password login — its own
            // token family — so a real refresh is safe and REQUIRED: without
            // it the JWT carries no org claim and the entitlements check
            // below bounces the user back with member:false.
            if (typeof window !== 'undefined' && window.__E2E_DISABLE_SESSION_REFRESH) {
                try { await supabase.auth.refreshSession(); } catch { /* fall through to entitlements */ }
            } else {
                await ensureFreshSession();
            }

            // Post-login access state — now that the JWT carries the org claim,
            // the entitlements RPC resolves membership, plan and billing state.
            // A restricted org keeps admins/owners in for the billing recovery
            // path; members are logged back out to the picker with a notice.
            const entitlements = await getOrganizationEntitlements().catch(() => null);
            if(entitlements?.member === true){
                queryClient.setQueryData(QueryKeys.organizationEntitlements.current(getCachedOrgID()), entitlements);
            }
            if(entitlements?.member === false){
                setHasOrganizationSelected(false);
                await logOutOfOrganization();
                Toast.show({type: 'error', text2: "You don't have access to that organization.", visibilityTime: 6000});
                loader.disable();
                return;
            }
            // Blocked access states: owner/admin keep a recovery path into the
            // app (billing lives under Settings → Organization); members are
            // logged back out to the picker with a notice. hasOrganizationSelected
            // must reset or the render below would redirect into (tabs) anyway.
            const canFixBilling = entitlements?.isAdmin === true || entitlements?.isAccountOwner === true;
            if(entitlements?.member === true && !canFixBilling && (isBillingRestricted(entitlements) || isSetupPending(entitlements))){
                setHasOrganizationSelected(false);
                await logOutOfOrganization();
                Toast.show({
                    type: 'error',
                    text1: isBillingRestricted(entitlements) ? "Billing Restricted" : "Setup Not Complete",
                    text2: isBillingRestricted(entitlements)
                        ? "This organization's billing needs attention. An admin or the account owner can resolve it from billing settings."
                        : "This organization's setup isn't finished yet. An admin or the account owner can complete billing setup to activate it.",
                    visibilityTime: 6000
                });
                loader.disable();
                return;
            }
            if(isSetupPending(entitlements) && canFixBilling){
                Toast.show({type: 'info', text2: "Finish setting up billing from Settings → Organization → Billing.", visibilityTime: 6000});
            }

            const loggedInUser = await queryClient.fetchQuery({queryKey: ["loggedInUser"], queryFn: () => getLoggedInUserInfo()})
            const themeSetting = loggedInUser?.userSettings?.theme;

            auth.setTheme(loggedInUser && (!themeSetting || themeSetting == 'default') ? scheme : themeSetting);

            // Explicitly navigate to home tab to avoid landing on previous tab (e.g., settings)
            router.replace("/(tabs)/(home)");
        }
        else{
            Toast.show({text1: "Error", text2: "Something went wrong logging into that organization", type: "error"});
        }

        loader.disable();
    }

    const renderItem = ({item}) => {
        const org = item.Organization;
        // Plan-aware entry state: Free orgs never have a subscription; Paid
        // orgs keep the billingStatus signal until the post-login
        // entitlements RPC resolves the real accessState.
        const planKnown = org?.softwarePlan === 'free' || org?.softwarePlan === 'paid';
        const entryAllowed = planKnown || isBillingAllowed(org?.billingStatus);
        const billingIssue = org?.softwarePlan !== 'free' && planKnown && !isBillingAllowed(org?.billingStatus);
        return (
            <CutCard
                testID={"Org-" + org?.name}
                style={styles.cardFrame}
                contentStyle={styles.cardContent}
                onPress={item.pending ? undefined : () => signIntoOrganization(org)}
                accentColor={item.pending ? "warning" : "primary"}
            >
                <View style={{width:50, alignItems:"center", justifyContent:"center"}}>
                    {org?.logoPublicUrl ? <Image style={{width:50, height:50}} source={{uri: org?.logoPublicUrl}}/> : <Unicon name={"business"} colorVariant={"textSecondary"} size={40}/>}
                </View>
                <View style={{marginLeft:12, paddingLeft:10, flexGrow:1, flexShrink:1}}>
                    <Text numberOfLines={1} style={styles.header}>{org?.name}</Text>
                    <Text numberOfLines={1} style={{color: colors.textSecondary, fontSize:12, paddingTop:5, fontFamily:fonts.body}}>{org?.handle}</Text>
                    { item.pending &&
                        <Text style={{color: colors.warning, fontSize:13, paddingTop:5, fontStyle:"italic"}}>Pending Approval</Text>
                    }
                    { !item.pending && billingIssue &&
                        <Text style={{color: colors.warning, fontSize:12, paddingTop:5, fontFamily:fonts.body}}>Billing issue</Text>
                    }
                    { !item.pending && !entryAllowed &&
                        <Text style={{color: colors.warning, fontSize:12, paddingTop:5, fontFamily:fonts.body}}>No active subscription</Text>
                    }
                </View>
                { !item.pending &&
                    <View style={{flexDirection:"row", alignItems:"center", gap:8}}>
                        { planKnown &&
                            <Chip
                                testID={"PlanChip-" + org?.name}
                                colorVariant={org?.softwarePlan === 'paid' ? 'primary' : 'textSecondary'}
                                style={{borderRadius: 3}}
                            >
                                {org?.softwarePlan === 'paid' ? 'Complete' : 'Essentials'}
                            </Chip>
                        }
                        <Unicon name="chevron-forward-outline" size={25} colorVariant={"text"}/>
                    </View>
                }
            </CutCard>
        );
    }

    const renderInviteItem = ({item}) => {
        const isAccepting = acceptingInviteID === item.id;
        return (
            <CutCard style={styles.cardFrame} accentColor="primary" contentStyle={styles.cardContent}>
                <View style={{width:50, alignItems:"center", justifyContent:"center"}}>
                    {item.Organization?.logoPublicUrl ? <Image style={{width:50, height:50}} source={{uri: item.Organization?.logoPublicUrl}}/> : <Unicon name={"business"} colorVariant={"textSecondary"} size={40}/>}
                </View>
                <View style={{marginLeft:12, paddingLeft:10, flexGrow:1, flexShrink:1}}>
                    <Text numberOfLines={1} style={styles.header}>{item.Organization?.name}</Text>
                    <Text style={styles.inviteLabel}>You&apos;ve been invited to join</Text>
                </View>
                <TouchableOpacity
                    testID={"Accept-Invite-" + item.Organization?.name}
                    style={styles.acceptButton}
                    disabled={isAccepting}
                    onPress={() => handleAcceptInvite(item)}
                >
                    {isAccepting
                        ? <ActivityIndicator size={"small"} color={"#fff"} />
                        : <Text style={styles.acceptButtonText}>Accept</Text>
                    }
                </TouchableOpacity>
            </CutCard>
        )
    }

    const allOrganizations = useMemo(() => {
        if(isPending || fetchingPendingRequests){
            return []
        }
        return [...(organizations || []), ...(pendingOrganizations?.map((org) => ({...org, pending: true})) || [])]
            .sort((a, b) => (a?.Organization?.name || a?.name || '').localeCompare(b?.Organization?.name || b?.name || '', undefined, { sensitivity: 'base' }))
    }, [organizations, pendingOrganizations])
    
    return (isCheckingAuth 
        ? <ActivityIndicator size={"large"} color={colors.primary} animating={true} /> 
        : !isAuthenticated
            ? <Redirect href="/auth/login" />
            : hasOrganizationSelected
                ? <Redirect href="/(tabs)/(home)" />
                : <>
        <View style={{flex:1, backgroundColor:colors.background}}>
            <PatternBackground />
            <View style={{flex:1, alignItems:"center", paddingTop:insets.top + 64, paddingBottom:insets.bottom + 16, paddingHorizontal:16}}>
                <View style={{maxWidth:700, width:"100%", flex:1}}>
                    <LegendList recycleItems keyExtractor={(item) => item.organizationID}
                        keyboardShouldPersistTaps="handled"
                        estimatedItemSize={75}
                        data={allOrganizations}
                        contentContainerStyle={{paddingBottom:10, paddingHorizontal:10}}
                        renderItem={renderItem}
                        refreshing={false}
                        onRefresh={() => {
                            queryClient.invalidateQueries({queryKey: QueryKeys.organization.all()})
                            queryClient.invalidateQueries({queryKey: QueryKeys.registrations.all()})
                            queryClient.invalidateQueries({queryKey: QueryKeys.invites.forUser(currentAuthUser?.email)})
                        }}
                        ListHeaderComponent={<View>
                            <AuthHeader eyebrow="Your Reach community" title="Choose your organization" />
                            {pendingInvites?.length > 0 && !fetchingInvites && (
                                <View style={{marginBottom:10}}>
                                    <Text style={styles.sectionHeader}>Pending Invitations</Text>
                                    {pendingInvites.map((invite) => (
                                        <View key={invite.id}>{renderInviteItem({item: invite})}</View>
                                    ))}
                                </View>
                            )}
                            {allOrganizations?.length > 0 && pendingInvites?.length > 0 &&
                                <Text style={styles.sectionHeader}>Your Organizations</Text>
                            }
                        </View>}
                        ListFooterComponent={!isPending && !fetchingPendingRequests
                            ? <View>
                                {(organizations?.length == 0 && pendingOrganizations?.length == 0 && (!pendingInvites || pendingInvites?.length == 0)) && 
                                    <View style={{ alignItems:"center"}}>
                                        <EmptyListPlaceholder noLottie textColor={colors.text} spacing={20} message="Looks like you're not a part of any organizations! Request to join one with the button below. "/>
                                        <SolidButton text="Join an Organization" onPress={() => {
                                            setJoinModalOpen(true)
                                        }} />
                                        <View style={{marginTop:10}}>
                                            <SolidButton text="Enroll a New Organization" variant="outlined" testID="enroll-organization" onPress={() => openOrgSignup(isWeb)} />
                                        </View>
                                    </View>
                                }
                            </View>
                            : <View style={{ justifyContent:"center", alignItems:"center", marginTop:20}}><ActivityIndicator size={"large"} color={colors.primary} animating={true} /></View>
                        }
                    />
                </View>
                <View style={{width:'100%', maxWidth:700, paddingTop:12, borderTopWidth:1, borderTopColor:colors.border}}>
                    { (organizations?.length > 0 || pendingOrganizations?.length > 0) &&
                        <TouchableOpacity style={styles.bottomButton} onPress={() => {
                            setJoinModalOpen(true)
                        }}>
                            <View><Text style={{color:colors.text, fontFamily:fonts.body, fontSize:16}}>Join a New Organization</Text></View>
                            <View style={{marginTop:2}}><Unicon name="chevron-forward-outline" colorVariant={"text"} size={18} /></View>
                        </TouchableOpacity>
                    }

                    <TouchableOpacity testID="enroll-organization-bottom" style={styles.bottomButton} onPress={() => openOrgSignup(isWeb)}>
                        <View><Text style={{color:colors.text, fontFamily:fonts.body, fontSize:16}}>Enroll a New Organization</Text></View>
                        <View style={{marginTop:2}}><Unicon name="chevron-forward-outline" colorVariant={"text"} size={18} /></View>
                    </TouchableOpacity>

                    <TouchableOpacity style={styles.bottomButton} onPress={async () => {
                        await signOut('local', 'select-organization-user-requested');
                        router.push("/auth/login");
                    }}>
                        <View><Text style={{color:colors.text, fontFamily:fonts.body, fontSize:16}}>Sign Out</Text></View>
                        <View style={{marginTop:2}}><Unicon name="chevron-forward-outline" colorVariant={"text"} size={18} /></View>
                    </TouchableOpacity>
                </View>
            </View>
        </View>

        <SheetModal isOpen={joinModalOpen} onClose={() => setJoinModalOpen(false)} snapPoints={[800]} title="Join an Organization">

            <View style={{zIndex:2, alignItems:"center", marginTop:10}}>
                <Text style={styles.header}>Enter Your Organization Handle</Text>
                <Text style={styles.sub_header}>An organization handle is like a username for your church or organization. It&apos;s how we know the church you want to connect to! If you don&apos;t know it, reach out to an administrator or a member of your organization.</Text>
                <Text style={styles.sub_header}>After an admin of the organization approves your request, you will be able log in to it.</Text>
            </View>

            <View>
                <Input
                    filled
                    isNotOnSurface
                    title="Handle"
                    value={organizationHandle}
                    onUpdate={(new_value) => {
                        setOrganizationHandle(new_value.trim().toLowerCase());
                    }}
                />
            </View>

            <View style={{width:"100%",marginTop:20}}>
                <SolidButton processing={signingIn} onPress={async () => {
                    Keyboard.dismiss();

                    if(!organizationHandle){
                        Toast.show({type: 'error', text2: "Please enter the handle for the church or organization you'd like to join!"})
                        return false
                    }

                    const {data: currentUser, error: curentUserError} = await supabase.auth.getUser();

                    // Expected session-expired state — not an unexpected error, don't report to Sentry (CHURCH-APP-14R)
                    const isSessionExpired = curentUserError?.name === 'AuthSessionMissingError'
                        || curentUserError?.message?.includes('Auth session missing')
                        || (!curentUserError && !currentUser?.user);

                    if(isSessionExpired){
                        Sentry.logger.warn('Auth session missing when verifying user account', {query: "getCurrentUser", error: curentUserError});
                        Toast.show({type: 'error', text2: "Your session has expired. Please sign in again."});
                        await signOut('local', 'select-organization-session-missing');
                        router.push("/auth/login");
                        return false;
                    }

                    if(curentUserError || !currentUser?.user){
                        ErrorHandler(Error(curentUserError?.message || "Unable to verify user account"), {query: "getCurrentUser", error: curentUserError}, `Something went wrong verifying your user account`);
                        return false;
                    }

                    const {data: targetOrg, error: orgError} = await supabase.from('Organizations').select('id, name, handle').eq('handle', organizationHandle).maybeSingle();
                    
                    if(orgError || !targetOrg){
                        Toast.show({type: 'error', text2: "Looks like we couldn't find that organization. Make sure you have entered the correct handle!"})
                        return false;
                    }

                    const alreadyInOrg = organizations?.some(org => org.organizationID === targetOrg.id);
                    
                    if(alreadyInOrg){
                        Toast.show({type: 'error', text2: "You are already a member of this organization!"})
                        return false;
                    }

                    const hasPendingRegistration = pendingOrganizations?.some(reg => reg.organizationID === targetOrg.id);
                    
                    if(hasPendingRegistration){
                        Toast.show({type: 'error', text2: "You already have a pending registration request to this organization. An administrator will review your request."})
                        return false;
                    }

                    const hasPendingInvite = pendingInvites?.some(invite => invite.organizationID === targetOrg.id);
                    
                    if(hasPendingInvite){
                        Toast.show({type: 'error', text2: "You have a pending invitation to this organization. Check your invitations to accept it."})
                        return false;
                    }

                    saveRegistrationMutation.mutate({organizationHandle, user: currentUser.user});
                    
                    
                }} text="Request to Join" size="md"/>
            </View>
            
        </SheetModal>
    </>
    )
}


const styles = StyleSheet.create(({colors, fonts}) => ({
    header: {
        fontSize:15,
        textAlign:"left",
        fontFamily: fonts.bold,
        color: colors.text,
    },
    sub_header: {
        marginTop: 10,
        fontSize:13,
        fontStyle:"italic",
        textAlign:"center",
        color: colors.textSecondary
    },
    sectionHeader: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 6,
        marginTop: 4,
    },
    cardFrame: {
        marginVertical: 5,
    },
    cardContent:{
        flexDirection:'row',
        alignItems:'center',
        padding: 15,
        justifyContent: 'space-between',
    },

    inviteLabel: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.primary,
        fontStyle: 'italic',
        paddingTop: 4,
    },
    acceptButton: {
        backgroundColor: colors.primary,
        paddingHorizontal: 16,
        paddingVertical: 8,
        borderRadius: 3,
        minWidth: 70,
        alignItems: 'center',
    },
    acceptButtonText: {
        color: colors.onPrimary,
        fontFamily: fonts.bold,
        fontSize: 13,
    },
    bottomButton: {
        zIndex:2, 
        flexDirection:"row", 
        alignItems:"center", 
        justifyContent:"center",
        marginVertical:10,
        _web:{
            _hover:{
                cursor:"pointer",
                opacity:0.8
            }
        }
    }
}))

export default OrgSelectionScreen;